import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Video, Save, CheckCircle, AlertCircle, Loader2,
  Youtube, ExternalLink, Lock, Unlock, Trash2, Plus
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVideosPage = () => {
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/lessons`);
      setLessons(response.data);
    } catch (error) {
      console.error('Failed to fetch lessons:', error);
      setMessage({ type: 'error', text: 'Failed to load lessons' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (lessonId, field, value) => {
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const extractYoutubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const saveLesson = async (lesson) => {
    setSaving(prev => ({ ...prev, [lesson.id]: true }));
    setMessage({ type: '', text: '' });

    try {
      await axios.put(`${API}/lessons/${lesson.id}`, {
        youtube_url: lesson.youtube_url,
        title: lesson.title,
        description: lesson.description,
        duration: lesson.duration,
        is_free: lesson.is_free,
        module: lesson.module
      });

      // Update local state with youtube_id
      const youtube_id = extractYoutubeId(lesson.youtube_url);
      setLessons(prev => prev.map(l => 
        l.id === lesson.id ? { ...l, youtube_id } : l
      ));

      setMessage({ type: 'success', text: `"${lesson.title}" saved successfully!` });
    } catch (error) {
      console.error('Failed to save lesson:', error);
      setMessage({ type: 'error', text: `Failed to save "${lesson.title}"` });
    } finally {
      setSaving(prev => ({ ...prev, [lesson.id]: false }));
    }
  };

  const toggleFreeStatus = async (lesson) => {
    const updatedLesson = { ...lesson, is_free: !lesson.is_free };
    handleInputChange(lesson.id, 'is_free', !lesson.is_free);
    await saveLesson(updatedLesson);
  };

  const getYoutubeThumbnail = (youtubeId) => {
    if (!youtubeId) return null;
    return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
  };

  if (loading) {
    return (
      <div className="page admin-page">
        <Header onEnrollClick={() => window.location.href = '/pricing'} />
        <main className="page-content">
          <div className="loading-state">
            <Loader2 className="spinner" />
            <p>Loading lessons...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page admin-page">
      <Header onEnrollClick={() => window.location.href = '/pricing'} />
      
      <main className="page-content">
        <section className="page-hero admin-hero">
          <div className="container">
            <span className="section-tag">Admin Panel</span>
            <h1 className="page-title">Manage Course Videos</h1>
            <p className="page-subtitle">
              Add YouTube video URLs to populate your course preview lessons
            </p>
          </div>
        </section>

        <section className="admin-content">
          <div className="container">
            {message.text && (
              <div className={`admin-message ${message.type}`}>
                {message.type === 'success' ? (
                  <CheckCircle size={18} />
                ) : (
                  <AlertCircle size={18} />
                )}
                <span>{message.text}</span>
              </div>
            )}

            <div className="admin-info-box">
              <Youtube className="info-icon" />
              <div>
                <h3>How to Add Videos</h3>
                <p>
                  1. Upload your video to YouTube<br />
                  2. Copy the video URL (e.g., https://youtube.com/watch?v=xxxxx)<br />
                  3. Paste it in the YouTube URL field below<br />
                  4. Click Save to update the lesson
                </p>
              </div>
            </div>

            <div className="lessons-admin-list">
              {lessons.map((lesson) => (
                <div key={lesson.id} className={`lesson-admin-card ${lesson.youtube_id ? 'has-video' : ''}`}>
                  <div className="lesson-admin-header">
                    <div className="lesson-number-badge">
                      Lesson {lesson.lesson_number || lesson.id}
                    </div>
                    <div className="lesson-status">
                      {lesson.youtube_id ? (
                        <span className="status-badge success">
                          <CheckCircle size={14} /> Video Added
                        </span>
                      ) : (
                        <span className="status-badge pending">
                          <AlertCircle size={14} /> No Video
                        </span>
                      )}
                      <button 
                        className={`free-toggle ${lesson.is_free ? 'free' : 'locked'}`}
                        onClick={() => toggleFreeStatus(lesson)}
                        title={lesson.is_free ? 'Click to make premium' : 'Click to make free'}
                      >
                        {lesson.is_free ? <Unlock size={14} /> : <Lock size={14} />}
                        {lesson.is_free ? 'Free' : 'Premium'}
                      </button>
                    </div>
                  </div>

                  <div className="lesson-admin-content">
                    <div className="lesson-preview">
                      {lesson.youtube_id ? (
                        <div className="video-thumbnail">
                          <img 
                            src={getYoutubeThumbnail(lesson.youtube_id)} 
                            alt={lesson.title}
                          />
                          <a 
                            href={`https://youtube.com/watch?v=${lesson.youtube_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="preview-link"
                          >
                            <ExternalLink size={16} /> Preview
                          </a>
                        </div>
                      ) : (
                        <div className="video-placeholder-admin">
                          <Video size={32} />
                          <span>No video yet</span>
                        </div>
                      )}
                    </div>

                    <div className="lesson-form">
                      <div className="form-group">
                        <label>Module</label>
                        <input
                          type="text"
                          value={lesson.module || ''}
                          onChange={(e) => handleInputChange(lesson.id, 'module', e.target.value)}
                          placeholder="e.g., Module 1: Foundations"
                        />
                      </div>

                      <div className="form-group">
                        <label>Title</label>
                        <input
                          type="text"
                          value={lesson.title || ''}
                          onChange={(e) => handleInputChange(lesson.id, 'title', e.target.value)}
                          placeholder="Lesson title"
                        />
                      </div>

                      <div className="form-group">
                        <label>Description</label>
                        <textarea
                          value={lesson.description || ''}
                          onChange={(e) => handleInputChange(lesson.id, 'description', e.target.value)}
                          placeholder="Brief description of the lesson"
                          rows={2}
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>Duration</label>
                          <input
                            type="text"
                            value={lesson.duration || ''}
                            onChange={(e) => handleInputChange(lesson.id, 'duration', e.target.value)}
                            placeholder="e.g., 15 min"
                          />
                        </div>
                      </div>

                      <div className="form-group youtube-input">
                        <label>
                          <Youtube size={16} /> YouTube URL
                        </label>
                        <input
                          type="text"
                          value={lesson.youtube_url || ''}
                          onChange={(e) => handleInputChange(lesson.id, 'youtube_url', e.target.value)}
                          placeholder="https://youtube.com/watch?v=... or https://youtu.be/..."
                        />
                      </div>

                      <Button
                        className="btn-primary save-btn"
                        onClick={() => saveLesson(lesson)}
                        disabled={saving[lesson.id]}
                      >
                        {saving[lesson.id] ? (
                          <><Loader2 className="spinner" /> Saving...</>
                        ) : (
                          <><Save size={16} /> Save Changes</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="admin-actions">
              <Link to="/preview">
                <Button className="btn-secondary">
                  <ExternalLink size={16} /> View Preview Page
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AdminVideosPage;
